import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { doGetClass } from '../../../store/class.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import ClassInfo from './Info';
import ClassRegister from './Register';
import { doGetClassStudents } from '../../../store/classStudent.slice';
import Loader from '../../Loader';
import ClassSchedule from './Schedule';
import { doGetClassTimetables } from '../../../store/classTimetable.slice';
import ClassScore from './Score';
import QueryString from 'qs';
import TabItem from './TabItem';
import ClassNotice from './Notice';
import ClassAward from './Award';
import TestContainer from './TestContainer';
import ClassPoint from './Point';
import useNotification from '../../../hooks/useNotification';

interface IProps {
  tab: string;
  setTab: (tab: string) => void;
  setIsExtended: (isExtended: boolean) => void;
  setRefreshTable: (refreshTable: boolean) => void;
}

const ClassTab: FC<IProps> = ({
  tab,
  setTab,
  setIsExtended,
  setRefreshTable,
}) => {
  const { t } = useTranslation();
  const { warningNotification, errorNotification } = useNotification();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { chosenClass, isLoading: isLoading1 } = useSelector((state: RootState) => state.class);
  // 불러둔 store의 내용을 하위 component에서 사용
  const { isLoading: isLoading2 } = useSelector((state: RootState) => state.classStudent);
  const { isLoading: isLoading3 } = useSelector((state: RootState) => state.classTimetable);

  const [classCode, setClassCode] = useState('');
  const [prevTab, setPrevTab] = useState('');
  const [statuses, setStatuses] = useState<any>(['','','']);

  useEffect(() => {
    if (query.tab && query.tab === 'createClass') {
      setClassCode('');
    }
    if (query.classCode) {
      const _classCode = String(query.classCode);
      if (
        _classCode !== classCode ||
        (tab !== prevTab && (prevTab === 'createClass' || prevTab === 'updateClass'))
      ) {
        doGetClassData(_classCode);
        setPrevTab(tab);

        // tab별로 진입점 이동
        switch (tab) {
          case 'createClass':
          case 'updateClass':
            setTab('info');
            setPrevTab('info');
            break;
          case 'allocateStudent':
          case 'allocateResult':
          case 'attendance':
          case 'refund':
          case 'retire':
          case 'retireResult':
          case 'transfer':
          case 'transferCalculation':
            setTab('register');
            setPrevTab('register');
            break;
          case 'calendarDetail':
          case 'rollbook':
          case 'inputScore':
            setTab('schedule');
            setPrevTab('schedule');
            break;
          case 'testScore':
            setTab('test');
            setPrevTab('test');
            break;
        }
      }
    }
  }, [location]);

  useEffect(() => {
    if (statuses.every((status: any) => status)) {
      sessionStorage.removeItem('statuses');

      if (statuses.includes('401')) {
        dispatch(doRemoveAuth());
      } else if (statuses.includes('403')) {
        warningNotification(t('common.message.1'));
        history.goBack();
      } else if (!statuses.every((status: any) => status === '200')) {
        errorNotification(t('common.message.error'));        
      }
    }
  }, [statuses]);

  const doGetClassData = (classCode: string) => {
    getClass(classCode);
    getStudents(classCode);
    getTimetables(classCode);
    setClassCode(classCode);
  }

  const onSetStatuses = (index: number, status: number) => {
    let newStatuses: any = sessionStorage.getItem('statuses');

    if (newStatuses && newStatuses !== 'null') {
      newStatuses = JSON.parse(newStatuses);
    } else {
      newStatuses = [...statuses];
    }

    newStatuses[index] = String(status);
    setStatuses(newStatuses);
    sessionStorage.setItem('statuses', JSON.stringify(newStatuses));
  };

  const getClass = async (classCode: string) => {
    const res = await dispatch(doGetClass(classCode));
    onSetStatuses(0, res?.status);
  };

  const getStudents = async (classCode: string) => {
    const res = await dispatch(doGetClassStudents(classCode));
    onSetStatuses(1, res?.status);
  };

  const getTimetables = async (classCode: string) => {
    const res = await dispatch(doGetClassTimetables(classCode));
    onSetStatuses(2, res?.status);
  };

  const renderTabContent = () => {
    switch (tab) {
      // 기본정보 탭
      case 'info':
      case 'createClass':
      case 'updateClass':
        return (
          <ClassInfo
            tab={tab}
            setTab={onSetTab}
            doGetClassData={doGetClassData}
            setRefreshTable={setRefreshTable}
          />
        );
      // 배치정보 탭
      case 'register':
      case 'allocateStudent':
      case 'allocateResult':
      case 'attendance':
      case 'refund':
      case 'retire':
      case 'retireResult':
      case 'transfer':
      case 'transferCalculation':
        return (
          <ClassRegister
            tab={tab}
            setTab={onSetTab}
            getStudents={getStudents}
          />
        );
      // 시간표 탭
      case 'schedule':
      case 'calendarDetail':
      case 'rollbook':
      case 'inputScore':
        return (
          <ClassSchedule
            tab={tab}
            setTab={onSetTab}
            setIsExtended={setIsExtended}
            getTimetables={getTimetables}
          />
        );
      // 성적 탭
      case 'score':
        return (
          <ClassScore
            setTab={onSetTab}
            setIsExtended={setIsExtended}
          />
        );
      // 포인트 탭
      case 'point':
        return <ClassPoint />;
      // 알림장 탭
      case 'notice':
        return (
          <ClassNotice
            classCode={classCode}
          />
        );
      // Test 탭
      case 'test':
      case 'testScore':
        return (
          <TestContainer
            tab={tab}
            setTab={onSetTab}
          />
        );
      // Star Award 탭
      case 'award':
        return <ClassAward />;
    }
  };

  const onSetTab = (
    tab: string,
    classCode: string = String(query.classCode),
  ) => {
    const param = QueryString.stringify({ ...query, tab, classCode });
    history.push(`/class/class?${param}`);
  };

  return (
    <div>
      {(isLoading1 || isLoading2 || isLoading3) && <Loader />}

      <div className="card card-tabs">
        <div className="card-header p-0 pt-1 border-bottom-0">
          <ul className="nav nav-tabs" id="custom-tabs-tab" role="tablist">
            <TabItem
              tab={tab}
              setTab={onSetTab}
              menu="info"
              menus="info,createClass,updateClass"
              text={t('student.student.tab1')}
            />
            <TabItem
              tab={tab}
              setTab={onSetTab}
              menu="register"
              menus="register,allocateStudent,allocateResult,attendance,refund,retire,retireResult,transfer,transferCalculation"
              text={t('class.class.tab1')}
            />
            <TabItem
              tab={tab}
              setTab={onSetTab}
              menu="schedule"
              menus="schedule,calendarDetail,rollbook,inputScore"
              text={t('class.class.tab2')}
            />
            <TabItem
              tab={tab}
              setTab={onSetTab}
              menu="score"
              menus="score"
              text={t('class.class.tab3')}
            />
            <TabItem
              tab={tab}
              setTab={onSetTab}
              menu="point"
              menus="point"
              text={t('class.class.tab5')}
            />
            <TabItem
              tab={tab}
              setTab={onSetTab}
              menu="notice"
              menus="notice"
              text={t('class.class.tab4')}
            />
            <TabItem
              tab={tab}
              setTab={onSetTab}
              menu="test"
              menus="test,testScore"
              text="TEST"
            />
            <TabItem
              tab={tab}
              setTab={onSetTab}
              menu="award"
              menus="award"
              text="Canb Award"
            />
          </ul>
        </div>
        <div className="card-body">
          <div className="tab-content" id="custom-tabs-tabContent">
            {chosenClass || tab === 'createClass' ? (
              renderTabContent()
            ) : (
                <div className="text-center">{t('class.class.text33')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassTab;
